import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { ProductCard } from "../components/ProductCard";
import { Reveal, MaskLine } from "../components/Reveal";
import { fetchProducts, gbp } from "../lib/api";
import { useCart } from "../context/CartContext";

const HERO_IMG = `${process.env.PUBLIC_URL}/hero-model.png`;
const HERO_IMG_MOBILE = `${process.env.PUBLIC_URL}/hero-model-mobile.png`;

const chapters = [
  { no: "01", ritual: "Energy", title: "Beauty from within", body: "Hair, skin & nail gummies with biotin, selenium and vitamin C — the daily foundation for a natural, lit-from-within glow." },
  { no: "02", ritual: "Sleep", title: "Rest, restored", body: "A calming berry blend of valerian root and L-theanine to help you unwind and wake up genuinely rested." },
  { no: "03", ritual: "Glow", title: "Elasticity & glow", body: "Marine collagen peptides with hyaluronic acid and vitamin C to support skin elasticity, hydration and joint health." },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.35, 0.7]);

  const scrollToProducts = () => document.getElementById("products")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} data-testid="hero" className="relative h-[100svh] min-h-[640px] overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0 scale-105 sm:scale-110">
        <picture>
          <source media="(max-width: 639px)" srcSet={HERO_IMG_MOBILE} />
          <img
            src={HERO_IMG}
            alt="Woman in a tropical garden — Prestige Wellness Co"
            className="block w-full h-full object-cover object-[70%_center] sm:object-center"
          />
        </picture>
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-gradient-to-b from-forest-deep/50 via-forest-deep/20 to-forest-deep/60" />
      <div className="absolute inset-0 bg-gradient-to-t from-forest-deep/60 to-transparent" />

      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-center">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 1 }}
          className="text-cream/90 text-xs md:text-sm tracking-[0.28em] uppercase font-bold mb-6"
        >
          Prestige Wellness Co — Gummy Supplements
        </motion.p>

        <h1 className="font-sans font-black tracking-tighter leading-[0.9] text-cream text-6xl sm:text-7xl md:text-8xl lg:text-[8.5rem]">
          <MaskLine delay={0.2}>Wellness</MaskLine>
          <MaskLine delay={0.35} className="font-serif italic font-medium text-[#E8A87C]">
            worth
          </MaskLine>
          <MaskLine delay={0.5}>chewing.</MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9 }}
          className="mt-8 max-w-md text-cream/85 font-serif text-xl md:text-2xl leading-relaxed"
        >
          Three daily rituals — energy, sleep and glow — engineered to actually taste good.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-10"
        >
          <button
            data-testid="hero-shop-button"
            onClick={scrollToProducts}
            className="group inline-flex items-center gap-3 rounded-full bg-forest text-cream px-8 py-4 md:px-10 md:py-5 text-sm font-bold tracking-widest uppercase transition-colors duration-300 hover:bg-forest-deep"
          >
            Shop the range
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={2} />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

const EditorialMarquee = () => (
  <div data-testid="marquee" className="bg-cream py-8 border-y border-forest/10">
    <Marquee speed={38} gradient={false} autoFill>
      {["Energy", "Sleep", "Glow", "Beauty", "Rest", "Radiance"].map((w, i) => (
        <span key={i} className="marquee-item text-4xl md:text-6xl text-forest/25 mx-8 md:mx-12">
          {w} <span className="text-[#E8A87C]/60">•</span>
        </span>
      ))}
    </Marquee>
  </div>
);

const Manifesto = () => (
  <section id="manifesto" data-testid="manifesto" className="bg-cream py-16 sm:py-24 md:py-36">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <Reveal>
        <p className="text-xs md:text-sm tracking-[0.28em] uppercase font-bold text-forest mb-4">The daily ritual</p>
        <h2 className="font-sans font-extrabold tracking-tight text-4xl md:text-6xl leading-[1.02] max-w-3xl text-ink">
          Three rituals, formulated to feel like a treat — not a tablet.
        </h2>
      </Reveal>

      <div className="mt-16 md:mt-24 grid md:grid-cols-3 gap-x-12 gap-y-14">
        {chapters.map((c, i) => (
          <Reveal key={c.no} delay={i * 0.12}>
            <div className="border-t border-forest/15 pt-6">
              <div className="flex items-baseline justify-between">
                <span className="font-serif text-6xl md:text-7xl text-forest/30 leading-none">{c.no}</span>
                <span className="font-serif italic text-faded text-lg">{c.ritual}</span>
              </div>
              <h3 className="mt-8 font-sans font-bold text-2xl tracking-tight text-ink">{c.title}</h3>
              <p className="mt-4 text-faded leading-relaxed">{c.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Bundles = ({ bundles }) => {
  const { addItem } = useCart();
  return (
    <section id="bundles" data-testid="bundles" className="bg-tan/70 py-16 sm:py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <p className="text-xs md:text-sm tracking-[0.28em] uppercase font-bold text-forest mb-4">Value bundles</p>
          <h2 className="font-sans font-extrabold tracking-tight text-4xl md:text-6xl leading-[1.02] max-w-2xl text-ink">
            Build the full ritual, <span className="font-serif italic font-medium">save more.</span>
          </h2>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-2 gap-6 md:gap-8">
          {bundles.map((b, i) => (
            <Reveal key={b.slug} delay={i * 0.1}>
              <div data-testid={`bundle-card-${b.slug}`} className="group relative rounded-[1.75rem] bg-offwhite border border-tan overflow-hidden flex flex-col md:flex-row min-h-[340px]">
                <div className="md:w-1/2 bg-white overflow-hidden">
                  <img src={b.image} alt={b.name} className="w-full h-full object-contain p-3 min-h-[220px] transition-transform duration-[900ms] group-hover:scale-105" />
                </div>
                <div className="md:w-1/2 p-7 md:p-9 flex flex-col">
                  <span className="self-start bg-forest text-cream text-[10px] font-bold tracking-[0.18em] uppercase px-3 py-1.5 rounded-full">{b.badge}</span>
                  <h3 className="mt-5 font-sans font-extrabold text-2xl md:text-3xl tracking-tight leading-tight text-ink">{b.name}</h3>
                  <p className="mt-3 text-sm text-faded leading-relaxed flex-1">{b.description}</p>
                  <div className="mt-6 flex flex-col gap-4">
                    <div className="flex flex-col">
                      <span className="font-sans font-black text-2xl text-ink">{gbp(b.price_onetime)}</span>
                      <span className="text-xs text-faded">Subscribe {gbp(b.price_subscribe)}</span>
                    </div>
                    <button
                      data-testid={`add-bundle-${b.slug}`}
                      onClick={() => addItem(b, "onetime", 1)}
                      className="group/b w-full flex items-center justify-center gap-2 rounded-full bg-forest text-cream px-6 py-3.5 text-xs font-bold tracking-widest uppercase transition-colors hover:bg-forest-deep"
                    >
                      Add bundle
                      <ArrowRight className="w-4 h-4 transition-transform group-hover/b:translate-x-1" strokeWidth={2} />
                    </button>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const ScienceShowcase = () => {
  const videoRef = useRef(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.setAttribute("muted", "");
    v.setAttribute("playsinline", "");
    const play = () => { const p = v.play(); if (p && p.catch) p.catch(() => {}); };
    play();
    v.addEventListener("loadeddata", play);
    v.addEventListener("canplay", play);
    return () => { v.removeEventListener("loadeddata", play); v.removeEventListener("canplay", play); };
  }, []);

  return (
    <section id="science" data-testid="science-showcase" className="relative h-[80vh] min-h-[520px] overflow-hidden bg-forest-deep">
      <video
        ref={videoRef}
        data-testid="showcase-video"
        className="absolute inset-0 w-full h-full object-cover"
        src={`${process.env.PUBLIC_URL}/showcase-video.mp4`}
        poster={`${process.env.PUBLIC_URL}/showcase-poster.jpg`}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-forest-deep via-forest-deep/45 to-forest-deep/55" />
      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-center">
        <Reveal>
          <p className="text-xs md:text-sm tracking-[0.28em] uppercase font-bold text-cream/80 mb-4">
            The science of glow
          </p>
          <h2 className="font-sans font-extrabold tracking-tight text-4xl md:text-6xl leading-[1.02] max-w-3xl text-cream">
            Clinically-minded formulas, <span className="font-serif italic font-medium text-[#E8A87C]">crafted like couture.</span>
          </h2>
          <p className="mt-6 max-w-lg text-cream/85 font-serif text-lg md:text-xl leading-relaxed">
            Every gummy is engineered with precision-dosed actives — the substance behind the glow.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "cancelled") {
      import("sonner").then(({ toast }) => toast("Checkout cancelled — your bag is saved."));
    }
  }, []);

  const core = products.filter((p) => !p.is_bundle);
  const bundles = products.filter((p) => p.is_bundle);

  return (
    <div className="bg-cream">
      <Navbar heroLight />
      <Hero />
      <EditorialMarquee />
      <Manifesto />
      <ScienceShowcase />

      <section id="products" data-testid="products" className="bg-cream py-16 sm:py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 sm:mb-14">
              <div>
                <p className="text-xs md:text-sm tracking-[0.28em] uppercase font-bold text-forest mb-4">Shop the range</p>
                <h2 className="font-sans font-extrabold tracking-tight text-4xl md:text-6xl leading-[1.02] max-w-2xl text-ink">
                  Your everyday <span className="font-serif italic font-medium">essentials.</span>
                </h2>
              </div>
              <p className="font-serif italic text-faded text-lg max-w-xs">Choose one-time or subscribe & save 15% on every order.</p>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {core.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {bundles.length > 0 && <Bundles bundles={bundles} />}
      <Footer />
    </div>
  );
}
